import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Round a monetary amount to exactly 2 decimal places
 * Handles JavaScript floating-point precision issues
 */
export function roundToTwoDecimals(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/**
 * Parse a string/number to a monetary amount with exactly 2 decimal places
 */
export function parseMonetaryAmount(value: string | number): number {
  const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
  return roundToTwoDecimals(numValue);
}

export function formatCurrency(amount: number): string {
  // Ensure the amount is properly rounded to 2 decimal places first
  const roundedAmount = roundToTwoDecimals(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundedAmount);
}

export function formatDate(dateString: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

export function formatTime(dateString: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

export function formatDateTime(date: string | Date): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return String(date);
  }
}

// Token management functions
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null; // Return null during SSR/build
  }
  
  try {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
}

export function setAccessToken(token: string): void {
  if (typeof window === 'undefined') {
    return; // Do nothing during SSR/build
  }
  
  try {
    localStorage.setItem('access_token', token);
    localStorage.setItem('token', token); // For backward compatibility
  } catch (error) {
    console.error('Error setting access token:', error);
  }
}

export function removeAccessToken(): void {
  if (typeof window === 'undefined') {
    return; // Do nothing during SSR/build
  }
  
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token'); // For backward compatibility
  } catch (error) {
    console.error('Error removing access token:', error);
  }
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    return localStorage.getItem('refresh_token');
  } catch (error) {
    console.error('Error accessing refresh token:', error);
    return null;
  }
}

export function setRefreshToken(token: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.setItem('refresh_token', token);
  } catch (error) {
    console.error('Error setting refresh token:', error);
  }
}

export function removeRefreshToken(): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.removeItem('refresh_token');
  } catch (error) {
    console.error('Error removing refresh token:', error);
  }
}

export function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (!token) {
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
  };
}

export function handleTokenInvalidation(): void {
  removeAccessToken();
  removeRefreshToken();
  
  // For static export, just redirect to login page
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

export function isTokenInvalidationError(status: number, errorData: any): boolean {
  // Check for common token invalidation patterns
  if (status === 401) {
    return true;
  }
  
  if (errorData?.code === 'token_not_valid' || 
      errorData?.detail?.includes('Invalid token') || 
      errorData?.detail?.includes('Authentication credentials')) {
    return true;
  }
  
  return false;
}

// API request functions
export async function makeAuthenticatedRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  
  if (!token) {
    handleTokenInvalidation();
    throw new Error('No access token available');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers as Record<string, string>
  };

  const config: RequestInit = {
    ...options,
    headers
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      if (isTokenInvalidationError(response.status, errorData)) {
        handleTokenInvalidation();
      }
      
      // Preserve the original error structure instead of creating a new Error
      const error = new Error() as any;
      Object.assign(error, errorData);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Authenticated request failed:', error);
    throw error;
  }
}

export async function makeRequest<T>(
  url: string,
  options: RequestInit = {},
  requireAuth: boolean = false
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };

  if (requireAuth) {
    const authHeaders = getAuthHeaders();
    Object.assign(headers, authHeaders);
  }

  const config: RequestInit = {
    ...options,
    headers
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      if (requireAuth && isTokenInvalidationError(response.status, errorData)) {
        handleTokenInvalidation();
      }
      
      // Preserve the original error structure instead of creating a new Error
      const error = new Error() as any;
      Object.assign(error, errorData);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
}

// Utility functions for API responses
export function handleApiError(error: any): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.detail) {
    return error.detail;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}

export function isNetworkError(error: any): boolean {
  return error instanceof TypeError && error.message.includes('fetch');
}

export function isServerError(status: number): boolean {
  return status >= 500;
}

export function isClientError(status: number): boolean {
  return status >= 400 && status < 500;
}

// Enhanced error handling with specific error types
export function getErrorMessage(error: any): string {
  // Handle network errors
  if (isNetworkError(error)) {
    return 'Network error. Please check your internet connection and try again.';
  }
  
  // Handle authentication errors
  if (error?.status === 401) {
    return 'Authentication failed. Please log in again.';
  }
  
  if (error?.status === 403) {
    return 'You do not have permission to perform this action.';
  }
  
  // Handle validation errors
  if (error?.status === 400) {
    if (error?.detail) {
      return error.detail;
    }
    if (error?.message) {
      return error.message;
    }
    return 'Invalid request. Please check your input and try again.';
  }
  
  // Handle server errors
  if (error?.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  // Handle the specific error format from backend: {"error": ["message"]}
  if (error?.error) {
    if (Array.isArray(error.error)) {
      return error.error[0];
    }
    return error.error;
  }
  
  // Handle specific field errors - check for nested error structure
  if (error?.email) {
    return Array.isArray(error.email) ? error.email[0] : error.email;
  }
  
  if (error?.password) {
    return Array.isArray(error.password) ? error.password[0] : error.password;
  }
  
  if (error?.username) {
    return Array.isArray(error.username) ? error.username[0] : error.username;
  }
  
  if (error?.name) {
    return Array.isArray(error.name) ? error.name[0] : error.name;
  }
  
  if (error?.phone) {
    return Array.isArray(error.phone) ? error.phone[0] : error.phone;
  }
  
  // Handle non-field errors
  if (error?.non_field_errors) {
    return Array.isArray(error.non_field_errors) ? error.non_field_errors[0] : error.non_field_errors;
  }
  
  // Handle error objects with nested structure (common in Django REST Framework)
  if (typeof error === 'object' && error !== null) {
    // Check for common field names in the error object
    const fieldNames = ['email', 'password', 'username', 'name', 'phone', 'first_name', 'last_name'];
    for (const fieldName of fieldNames) {
      if (error[fieldName]) {
        const fieldError = error[fieldName];
        if (Array.isArray(fieldError)) {
          return fieldError[0];
        }
        return fieldError;
      }
    }
    
    // Check for detail field (common in DRF)
    if (error.detail) {
      if (Array.isArray(error.detail)) {
        return error.detail[0];
      }
      return error.detail;
    }
    
    // Check for message field
    if (error.message) {
      return error.message;
    }
  }
  
  // Fallback to general error handling
  return handleApiError(error);
}

// Enhanced function to extract field-specific errors
export function getFieldErrors(error: any): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  
  if (typeof error === 'object' && error !== null) {
    // Handle the specific error format from backend: {"error": ["message"]}
    if (error.error) {
      if (Array.isArray(error.error)) {
        fieldErrors['general'] = error.error[0];
      } else {
        fieldErrors['general'] = error.error;
      }
      return fieldErrors;
    }
    
    // Common field names to check
    const fieldNames = [
      'email', 'password', 'username', 'name', 'phone', 
      'first_name', 'last_name', 'password_confirm', 'old_password',
      'new_password', 'new_password_confirm'
    ];
    
    for (const fieldName of fieldNames) {
      if (error[fieldName]) {
        const fieldError = error[fieldName];
        if (Array.isArray(fieldError)) {
          fieldErrors[fieldName] = fieldError[0];
        } else {
          fieldErrors[fieldName] = fieldError;
        }
      }
    }
    
    // Handle non-field errors (common in Django REST Framework)
    if (error.non_field_errors) {
      const nonFieldError = Array.isArray(error.non_field_errors) 
        ? error.non_field_errors[0] 
        : error.non_field_errors;
      fieldErrors['general'] = nonFieldError;
    }
    
    // Handle detail field (common in DRF)
    if (error.detail && !Object.keys(fieldErrors).length) {
      const detailError = Array.isArray(error.detail) 
        ? error.detail[0] 
        : error.detail;
      fieldErrors['general'] = detailError;
    }
  }
  
  return fieldErrors;
}

// Error handling with toast integration
export function handleErrorWithToast(error: any, toast: any, title: string = 'Error'): void {
  const message = getErrorMessage(error);
  
  toast({
    title,
    description: message,
    variant: 'destructive',
  });
}

// Success toast utility
export function showSuccessToast(toast: any, title: string, description: string): void {
  toast({
    title,
    description,
    variant: 'default',
  });
}

// Info toast utility
export function showInfoToast(toast: any, title: string, description: string): void {
  toast({
    title,
    description,
    variant: 'default',
  });
}

// Warning toast utility
export function showWarningToast(toast: any, title: string, description: string): void {
  toast({
    title,
    description,
    variant: 'destructive',
  });
} 

export function formatWeight(weight: number): string {
  return `${weight.toFixed(2)} g`;
}

// Utility function to check if a path is active
export function isActivePath(currentPath: string, targetPath: string): boolean {
  // Remove query parameters and normalize paths by removing trailing slashes
  const cleanCurrentPath = currentPath.split('?')[0].replace(/\/$/, '');
  const cleanTargetPath = targetPath.replace(/\/$/, '');
  
  // Exact match after normalization
  if (cleanCurrentPath === cleanTargetPath) {
    return true;
  }
  
  // Handle root path special case
  if (targetPath === '/' && (cleanCurrentPath === '' || cleanCurrentPath === '/')) {
    return true;
  }
  
  // For navigation items, we want to be more specific about matching
  // Only match exact paths or very specific parent-child relationships
  if (targetPath !== '/' && cleanCurrentPath.startsWith(cleanTargetPath)) {
    const remainingPath = cleanCurrentPath.slice(cleanTargetPath.length);
    
    // If no remaining path, it's an exact match
    if (remainingPath === '') {
      return true;
    }
    
    // For parent-child relationships, be very specific
    if (remainingPath.startsWith('/')) {
      const pathSegments = remainingPath.split('/').filter(segment => segment.length > 0);
      
      // Special case: /pawn should only match /pawn exactly, not /pawn/loans
      if (cleanTargetPath === '/pawn') {
        return false; // /pawn should not match any child paths
      }
      
      // For other paths, only allow exact matches or very specific children
      // But prefer exact matches over parent matches
      // For example, if we're on /pawn/loans/new, prefer /pawn/loans/new over /pawn/loans
      if (pathSegments.length === 1) {
        // Only match if this is the most specific match available
        // For parent-child relationships, be very restrictive
        const currentPathSegments = cleanCurrentPath.split('/').filter(segment => segment.length > 0);
        const targetPathSegments = cleanTargetPath.split('/').filter(segment => segment.length > 0);
        
        // If the target path is shorter than the current path, it's a parent
        // Only match if there's no exact child match available
        if (targetPathSegments.length < currentPathSegments.length) {
          // This is a parent match, but we should prefer exact matches
          // Only match if the remaining path is exactly one segment and no further nesting
          return pathSegments.length === 1 && !cleanCurrentPath.includes('/', cleanTargetPath.length + 1);
        }
        
        // If the target path is the same length or longer, it's an exact match or child
        return true;
      }
    }
  }
  
  return false;
} 

// New, simpler utility function to check if a path is active
export function isActivePathNew(currentPath: string, targetPath: string): boolean {
  // Remove query parameters and normalize paths by removing trailing slashes
  const cleanCurrentPath = currentPath.split('?')[0].replace(/\/$/, '');
  const cleanTargetPath = targetPath.replace(/\/$/, '');
  
  // Only exact matches
  return cleanCurrentPath === cleanTargetPath;
}

// Tenant utility functions for development
export function getTenantSlug(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    // First try to get from localStorage (for development)
    const devTenant = localStorage.getItem('dev_tenant_slug');
    if (devTenant) {
      return devTenant;
    }
    
    // Try to get from tenant store (for production)
    const tenantStore = localStorage.getItem('tenant-storage');
    if (tenantStore) {
      try {
        const tenantData = JSON.parse(tenantStore);
        if (tenantData?.state?.tenant?.slug) {
          return tenantData.state.tenant.slug;
        }
      } catch (e) {
        console.warn('Failed to parse tenant store data');
      }
    }
    
    // Fallback to user's tenant info
    const userTenant = localStorage.getItem('user_tenant_slug');
    if (userTenant) {
      return userTenant;
    }
    
    // Try to get from user data in localStorage
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user?.tenant?.slug) {
          return user.tenant.slug;
        }
      } catch (e) {
        console.warn('Failed to parse user data for tenant');
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting tenant slug:', error);
    return null;
  }
}

export function setTenantSlug(tenantSlug: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.setItem('dev_tenant_slug', tenantSlug);
    console.log('Set development tenant slug:', tenantSlug);
  } catch (error) {
    console.error('Error setting tenant slug:', error);
  }
}

export function clearTenantSlug(): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    localStorage.removeItem('dev_tenant_slug');
    localStorage.removeItem('user_tenant_slug');
    console.log('Cleared tenant slug');
  } catch (error) {
    console.error('Error clearing tenant slug:', error);
  }
}

export function getTenantHeaders(): Record<string, string> {
  const tenantSlug = getTenantSlug();
  if (tenantSlug) {
    return { 'X-Tenant': tenantSlug };
  }
  return {};
}

// Enhanced tenant headers function that works in both dev and production
export function getEnhancedTenantHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }
  
  try {
    // Try to get from tenant store first (most reliable)
    const tenantStore = localStorage.getItem('tenant-storage');
    if (tenantStore) {
      try {
        const tenantData = JSON.parse(tenantStore);
        if (tenantData?.state?.tenant?.slug) {
          return { 'X-Tenant': tenantData.state.tenant.slug };
        }
      } catch (e) {
        console.warn('Failed to parse tenant store data for headers');
      }
    }
    
    // Fallback to other methods
    return getTenantHeaders();
  } catch (error) {
    console.error('Error getting enhanced tenant headers:', error);
    return {};
  }
}

// Check if we're in development mode
export function isDevelopmentMode(): boolean {
  return process.env.NODE_ENV === 'development' || 
         typeof window !== 'undefined' && window.location.hostname === 'localhost';
} 

// Type-safe API response transformers
export const toNumber = (value: string | number | null | undefined, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

export const toCurrency = (value: string | number | null | undefined): number => {
  return toNumber(value, 0);
};

// Transform invoice data to match frontend expectations
export const transformInvoiceData = (invoice: any) => ({
  ...invoice,
  subtotal: toCurrency(invoice.subtotal),
  sgst_amount: toCurrency(invoice.sgst_amount),
  cgst_amount: toCurrency(invoice.cgst_amount),
  tax_amount: toCurrency(invoice.tax_amount),
  discount_amount: toCurrency(invoice.discount_amount),
  total_amount: toCurrency(invoice.total_amount),
  exchange_value: toCurrency(invoice.exchange_value),
  remaining_balance: toNumber(invoice.remaining_balance),
  items: invoice.items?.map((item: any) => ({
    ...item,
    quantity: toNumber(item.quantity),
    unit_price: toCurrency(item.unit_price),
    total_price: toCurrency(item.total_price),
    discount_amount: toCurrency(item.discount_amount),
    discount_percentage: toCurrency(item.discount_percentage),
    net_weight: toCurrency(item.net_weight),
    rate_per_gram: toNumber(item.rate_per_gram),
  })) || [],
});

// Transform advance payment data
export const transformAdvancePaymentData = (advance: any) => ({
  ...advance,
  amount: toCurrency(advance.amount),
  applied_amount: toCurrency(advance.applied_amount),
  remaining_amount: toCurrency(advance.remaining_amount),
});

// Transform item data
export const transformItemData = (item: any) => ({
  ...item,
  quantity: toNumber(item.quantity),
  gross_weight: toCurrency(item.gross_weight),
  net_weight: toCurrency(item.net_weight),
  stone_weight: toCurrency(item.stone_weight),
  making_charges: toCurrency(item.making_charges),
  stone_charges: toCurrency(item.stone_charges),
  other_charges: toCurrency(item.other_charges),
  purchase_price: toCurrency(item.purchase_price),
  selling_price: toCurrency(item.selling_price),
}); 